/// <reference path="../commonClasses/common.interface.ts" />
/// <reference path="../commonClasses/commonDataServiceBase.ts" />

module Meevo.Components.Common {

    export enum ComparedToEndChoicesEnum {
        PreviousDay = 0,
        SameDayPreviousMonth,
        SameDayPreviousWeek,
        SameDayPreviousYear,
        PreviousWeek,
        PreviousWeekToDate,
        SameWeekPreviousMonth,
        SameWeekToDatePreviousMonth,
        SameWeekPreviousYear,
        SameWeekToDatePreviousYear,
        PreviousMonth,
        PreviousMonthToDate,
        SameMonthPreviousYear,
        SameMonthToDatePreviousYear,
        SpecificMonth,
        SpecificMonthToDate,
        PreviousQuarter,
        PreviousQuarterToDate,
        SameQuarterPreviousYear,
        SameQuarterToDatePreviousYear,
        PreviousYear,
        PreviousYearToDate,
        SpecificYear,
        SpecificYearToDate,
        SpecificDate,
        SameDateRangePreviousYear
    }

    export interface IDateRangeObject {
        from: Date;
        to: Date;
    }

    export class DateRangeService {

        static factory(): Function {
            let instance = () => new DateRangeService();

            instance.$inject = [];

            return instance;
        }

        public today(): IDateRangeObject {
            return {
                from: moment().startOf('day').toDate(),
                to: moment().endOf('day').toDate()
            };
        }

        public previousDay(day?: Date): IDateRangeObject {
            return {
                from: moment(day).add(-1, 'days').startOf('day').toDate(),
                to: moment(day).add(-1, 'days').endOf('day').toDate()
            };
        }

        public thisWeek(date = new Date()): IDateRangeObject {
            return {
                from: moment(date).startOf('week').toDate(),
                to: moment(date).endOf('week').toDate()
            };
        }

        public weekToDate(date?: Date): IDateRangeObject {
            return {
                from: moment(date).startOf('week').toDate(),
                to: moment(date).endOf('day').toDate()
            };
        }

        public previousWeekToDate(dayFrom?: Date, dayTo?: Date): IDateRangeObject {
            const { from, to } = this.previousWeek(dayFrom, dayTo);

            return {
                from: from,
                to: moment(to).endOf('day').toDate()
            };
        }

        public previousWeek(dayFrom?: Date, dayTo?: Date): IDateRangeObject {
            let resultDayFrom = moment(dayFrom).subtract(1, 'weeks');
            let resultDayTo = moment(dayTo || dayFrom).subtract(1, 'weeks');

            if (!dayFrom) {
                resultDayFrom.startOf('week');
            }
            if (!dayTo) {
                resultDayTo.endOf('week');
            }

            return {
                from: resultDayFrom.toDate(),
                to: resultDayTo.toDate()
            };
        }

        public thisMonth(date = new Date()): IDateRangeObject {
            return {
                from: moment(date).startOf('month').toDate(),
                to: moment(date).endOf('month').toDate()
            };
        }

        public monthToDate(dayFrom: Date = new Date(), dayTo: Date = new Date()): IDateRangeObject {
            return {
                from: moment(dayFrom).startOf('month').toDate(),
                to: moment({
                    year: dayFrom.getFullYear(),
                    month: dayFrom.getMonth(),
                    day: dayTo.getDate()
                }).endOf('day').toDate()
            };
        }

        public previousMonth(dayFrom?: Date, dayTo?: Date): IDateRangeObject {
            let resultDayFrom = moment(dayFrom).subtract(1, 'months');
            let resultDayTo = moment(dayTo || dayFrom).subtract(1, 'months');

            if (!dayFrom) {
                resultDayFrom.startOf('month');
            }
            if (!dayTo) {
                resultDayTo.endOf('month');
            }

            return {
                from: resultDayFrom.toDate(),
                to: resultDayTo.toDate()
            };
        }

        public previousQuarter(startDate?: Date): IDateRangeObject {
            return this.getQuarter(true, startDate);
        }

        public previousQuarterToDate(_dayFrom?: Date, dayTo?: Date): IDateRangeObject {
            const thisQuarter = this.quarterToDate(false, dayTo);
            const diffMonth = moment(thisQuarter.to).diff(moment(thisQuarter.from), 'months');
            const { from } = this.previousQuarter(dayTo);

            return {
                from: from,
                to: moment(from)
                    .add(diffMonth, 'months')
                    .date(thisQuarter.to.getDate())
                    .toDate()
            };
        }

        public getQuarter(prev: boolean = false, startDate?: Date): IDateRangeObject {
            let quarter: number = moment(startDate).quarter(),
                from, to;

            if (prev) {
                quarter -= 1;
            }

            if (quarter === 0) { // prev quarter === Q4 prev year
                let prevYear = moment(startDate).year() - 1;

                from = moment(startDate).month(9).year(prevYear).startOf('month').toDate();
                to = moment(startDate).month(11).year(prevYear).endOf('month').toDate();
            } else {
                from = moment(startDate).month(quarter * 3 - 3).startOf('month').toDate();
                to = moment(startDate).month(quarter * 3 - 1).endOf('month').toDate();
            }

            return {
                from: from,
                to: to
            };
        }

        public quarterToDate(prev: boolean = false, startDate: Date = new Date()): IDateRangeObject {
            const { from, to } = this.getQuarter(prev, startDate);
            const toMoment = moment(to)
                .date(startDate.getDate());

            if (!prev) {
                toMoment
                    .month(startDate.getMonth());
            }

            return {
                from: from,
                to: toMoment.toDate()
            };
        }

        public previousYear(dayFrom?: Date, dayTo?: Date): IDateRangeObject {
            let resultDayFrom = moment(dayFrom).subtract(1, 'years');
            let resultDayTo = moment(dayTo).subtract(1, 'years');

            if (!dayFrom) {
                resultDayFrom.startOf('year');
            }
            if (!dayTo) {
                resultDayTo.endOf('year');
            }

            return {
                from: resultDayFrom.toDate(),
                to: resultDayTo.toDate()
            };
        }

        public thisYear(date = new Date()): IDateRangeObject {
            return {
                from: moment(date).startOf('years').toDate(),
                to: moment(date).endOf('years').toDate()
            };
        }

        public thisYearToDate(): IDateRangeObject {
            return {
                from: moment().startOf('years').toDate(),
                to: moment().endOf('day').toDate()
            };
        }

        public specificYearToDate(dayFrom?: Date, dayTo?: Date): IDateRangeObject {
            return {
                from: moment(dayFrom).startOf('years').toDate(),
                to: moment(dayTo).endOf('day').toDate()
            };
        }
    }

    angular.module('meevo.components.common')
        .factory('meevo.components.common.DateRangeService', DateRangeService.factory());
}
